import React, { useCallback, useEffect, useMemo, useState } from "react";
import io from "socket.io-client";
import { useNavigate } from "react-router-dom";
import {
  Avatar,
  Button,
  Card,
  Col,
  Divider,
  Empty,
  Form,
  Grid,
  Image,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
  Upload,
  message,
} from "antd";
import {
  DeleteOutlined,
  DollarOutlined,
  EditOutlined,
  InboxOutlined,
  PlusOutlined,
  SearchOutlined,
  ShoppingOutlined,
  StockOutlined,
  WarningOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { API_BASE, SOCKET_URL } from "../../config/api";
import { getAuthHeaders } from "../../utils/auth";

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const money = new Intl.NumberFormat("es-MX", {
  style: "currency",
  currency: "MXN",
  minimumFractionDigits: 2,
});

const initialValues = {
  clave: "",
  nombre: "",
  descripcion: "",
  usageType: "sale",
  stock: 0,
  existencia: 0,
  honorario: 0,
  precioLista: 0,
  precioPublicado: 0,
};

const metricCardStyle = {
  height: "100%",
  borderRadius: 16,
  border: "1px solid #eef2f6",
  boxShadow: "0 8px 22px rgba(15, 23, 42, 0.035)",
};

const productsTableWidth = 1251;

const renderProductImage = (image, size = 42) =>
  image ? (
    <Image
      width={size}
      height={size}
      preview={{ mask: "Ver" }}
      src={image}
      style={{ objectFit: "cover", borderRadius: 10 }}
    />
  ) : (
    <Avatar
      shape="square"
      size={size}
      icon={<ShoppingOutlined />}
      style={{ background: "#f1f5f9", color: "#64748b", borderRadius: 10 }}
    />
  );

const Products = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [saving, setSaving] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [form] = Form.useForm();
  const screens = useBreakpoint();

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/products`, {
        headers: getAuthHeaders(),
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.message || "No se pudieron cargar los productos");
      }

      setProducts(Array.isArray(data) ? data : []);
    } catch (error) {
      message.error(error.message || "No se pudieron cargar los productos");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  useEffect(() => {
    const socket = io(SOCKET_URL);
    socket.on("products:updated", fetchProducts);

    return () => {
      socket.off("products:updated", fetchProducts);
      socket.disconnect();
    };
  }, [fetchProducts]);

  const filteredProducts = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return products;

    return products.filter((product) =>
      [
        product.clave,
        product.nombre,
        product.descripcion,
        product.usageType === "internal" ? "uso interno" : "para vender",
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(normalizedQuery))
    );
  }, [products, query]);

  const totals = useMemo(
    () =>
      products.reduce(
        (accumulator, product) => ({
          stock: accumulator.stock + Number(product.stock || 0),
          existencia: accumulator.existencia + Number(product.existencia || 0),
          faltante: accumulator.faltante + Number(product.faltante || 0),
        }),
        { stock: 0, existencia: 0, faltante: 0 }
      ),
    [products]
  );

  const openCreateModal = () => {
    setEditingProduct(null);
    form.setFieldsValue(initialValues);
    setFileList([]);
    setIsModalOpen(true);
  };

  const openEditModal = (product) => {
    setEditingProduct(product);
    form.setFieldsValue({
      ...initialValues,
      ...product,
      stock: Number(product.stock || 0),
      existencia: Number(product.existencia || 0),
      honorario: Number(product.honorario || 0),
      precioLista: Number(product.precioLista || 0),
      precioPublicado: Number(product.precioPublicado || 0),
    });
    setFileList([]);
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const method = editingProduct ? "PUT" : "POST";
      const endpoint = editingProduct
        ? `${API_BASE}/products/${editingProduct.id}`
        : `${API_BASE}/products`;

      const payload = new FormData();
      Object.entries(values).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          payload.append(key, value);
        }
      });
      if (fileList[0]?.originFileObj) {
        payload.append("image", fileList[0].originFileObj);
      }

      const response = await fetch(endpoint, {
        method,
        headers: getAuthHeaders(),
        body: payload,
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.message || "No se pudo guardar el producto");
      }

      message.success(editingProduct ? "Producto actualizado" : "Producto creado");
      setIsModalOpen(false);
      fetchProducts();
    } catch (error) {
      if (error?.errorFields) return;
      message.error(error.message || "No se pudo guardar el producto");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (product) => {
    try {
      const response = await fetch(`${API_BASE}/products/${product.id}`, {
        method: "DELETE",
        headers: getAuthHeaders(),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data?.message || "No se pudo eliminar el producto");

      message.success("Producto eliminado");
      fetchProducts();
    } catch (error) {
      message.error(error.message || "No se pudo eliminar el producto");
    }
  };

  const columns = [
    {
      title: "Imagen",
      dataIndex: "imagen",
      align: "center",
      width: 72,
      render: (value) => renderProductImage(value, 38),
    },
    {
      title: "Clave",
      dataIndex: "clave",
      width: 88,
      render: (value) => <Text strong>{value}</Text>,
    },
    {
      title: "Nombre",
      dataIndex: "nombre",
      width: 130,
      render: (value) => <Text strong>{value}</Text>,
    },
    {
      title: "Descripción",
      dataIndex: "descripcion",
      width: 170,
      ellipsis: true,
      render: (value) => value ? <Tooltip title={value}><Text ellipsis>{value}</Text></Tooltip> : <Text type="secondary">Sin descripción</Text>,
    },
    {
      title: "Destino",
      dataIndex: "usageType",
      align: "center",
      width: 120,
      render: (value) =>
        value === "internal" ? (
          <Tag color="gold">Uso interno</Tag>
        ) : (
          <Tag color="blue">Para vender</Tag>
        ),
    },
    { title: "Stock", dataIndex: "stock", align: "center", width: 72 },
    { title: "Existencia", dataIndex: "existencia", align: "center", width: 90 },
    { title: "Faltante", dataIndex: "faltante", align: "center", width: 84, render: (value) => <Tag color={Number(value) > 0 ? "volcano" : "green"}>{value}</Tag> },
    { title: "Honorario", dataIndex: "honorario", align: "center", width: 90, render: (value) => `${Number(value || 0)}%` },
    { title: "Precio lista", dataIndex: "precioLista", width: 118, render: (value) => money.format(Number(value || 0)) },
    { title: "Precio público", dataIndex: "precioPublicado", width: 125, render: (value) => <Text strong>{money.format(Number(value || 0))}</Text> },
    {
      title: "Acciones",
      key: "actions",
      width: 92,
      render: (_, product) => (
        <Space>
          <Tooltip title="Editar producto">
            <Button type="text" icon={<EditOutlined />} onClick={() => openEditModal(product)} />
          </Tooltip>
          <Popconfirm
            title="¿Eliminar este producto?"
            description="Esta acción no se puede deshacer."
            okText="Eliminar"
            cancelText="Cancelar"
            okButtonProps={{ danger: true }}
            onConfirm={() => handleDelete(product)}
          >
            <Button danger type="text" icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const tableColumns = columns.map((column) => ({
    ...column,
    onHeaderCell: () => ({ style: { whiteSpace: "nowrap" } }),
  }));

  return (
    <div style={{ width: "100%", maxWidth: "none", margin: "0 auto", padding: screens.md ? "12px" : "0" }}>
      <Card
        bordered={false}
        style={{
          borderRadius: screens.md ? 20 : 0,
          border: screens.md ? "1px solid #eef2f6" : "none",
          boxShadow: screens.md ? "0 12px 32px rgba(15, 23, 42, 0.05)" : "none",
        }}
        styles={{ body: { padding: screens.md ? 28 : 16 } }}
      >
        <Row justify="space-between" align="middle" gutter={[16, 16]} style={{ marginBottom: 22 }}>
          <Col xs={24} lg={12}>
            <Space align="start" size={14}>
              <Avatar size={48} shape="square" icon={<ShoppingOutlined />} style={{ background: "#e6f4ff", color: "#1677ff", borderRadius: 14 }} />
              <div>
                <Text style={{ color: "#3b82f6", fontWeight: 700, fontSize: 12, letterSpacing: 1, textTransform: "uppercase" }}>Inventario del salón</Text>
                <Title level={2} style={{ margin: "3px 0 2px" }}>Productos</Title>
                <Text type="secondary">Administrá existencias, costos y precios de venta.</Text>
              </div>
            </Space>
          </Col>
          <Col xs={24} lg={12} style={{ textAlign: screens.lg ? "right" : "left" }}>
            <Space direction={screens.sm ? "horizontal" : "vertical"} wrap style={{ width: "100%", justifyContent: screens.lg ? "flex-end" : "flex-start" }}>
              <Button block={!screens.sm} size="large" icon={<DollarOutlined />} onClick={() => navigate("/admin/ventas-productos")}>Ver ventas y comisiones</Button>
              <Button block={!screens.sm} type="primary" size="large" icon={<PlusOutlined />} onClick={openCreateModal}>Nuevo producto</Button>
            </Space>
          </Col>
        </Row>

        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={12} sm={12} xl={6}><Card bordered={false} style={metricCardStyle}><Statistic title="Productos" value={products.length} prefix={<ShoppingOutlined style={{ color: "#3b82f6" }} />} /></Card></Col>
          <Col xs={12} sm={12} xl={6}><Card bordered={false} style={metricCardStyle}><Statistic title="Stock total" value={totals.stock} prefix={<StockOutlined style={{ color: "#8b5cf6" }} />} /></Card></Col>
          <Col xs={12} sm={12} xl={6}><Card bordered={false} style={metricCardStyle}><Statistic title="Existencia actual" value={totals.existencia} prefix={<InboxOutlined style={{ color: "#14b8a6" }} />} /></Card></Col>
          <Col xs={12} sm={12} xl={6}><Card bordered={false} style={metricCardStyle}><Statistic title="Faltantes" value={totals.faltante} valueStyle={{ color: totals.faltante ? "#fa541c" : "#16a34a" }} prefix={<WarningOutlined />} /></Card></Col>
        </Row>

        <Input
          allowClear
          size="large"
          prefix={<SearchOutlined style={{ color: "#94a3b8" }} />}
          placeholder="Buscar por clave, nombre o descripción"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          style={{ maxWidth: 520, margin: "24px 0 18px" }}
        />

        {filteredProducts.length || loading ? (
          screens.md ? (
            <Table
              rowKey="id"
              loading={loading}
              columns={tableColumns}
              dataSource={filteredProducts}
              tableLayout="fixed"
              scroll={{ x: productsTableWidth }}
              pagination={{ pageSize: 10, showSizeChanger: false }}
            />
          ) : (
            <Row gutter={[12, 12]}>
              {filteredProducts.map((product) => (
                <Col xs={24} key={product.id}>
                  <Card size="small" style={{ borderRadius: 14, border: "1px solid #eef2f6" }}>
                    <Row wrap={false} gutter={12} align="middle">
                      <Col>{renderProductImage(product.imagen, 52)}</Col>
                      <Col flex="auto">
                        <Text strong>{product.nombre}</Text><br />
                        <Text type="secondary">Clave: {product.clave}</Text>
                        <br />
                        <Tag color={product.usageType === "internal" ? "gold" : "blue"} style={{ marginTop: 6 }}>
                          {product.usageType === "internal" ? "Uso interno" : "Para vender"}
                        </Tag>
                      </Col>
                      <Col><Tag color={Number(product.faltante) > 0 ? "volcano" : "green"}>{product.faltante} faltante</Tag></Col>
                    </Row>
                    {product.descripcion && <Text type="secondary" style={{ display: "block", marginTop: 12 }}>{product.descripcion}</Text>}
                    <Row gutter={[8, 8]} style={{ marginTop: 14 }}>
                      <Col span={8}><Text type="secondary">Stock</Text><br /><Text strong>{product.stock}</Text></Col>
                      <Col span={8}><Text type="secondary">Existencia</Text><br /><Text strong>{product.existencia}</Text></Col>
                      <Col span={8}><Text type="secondary">Honorario</Text><br /><Text strong>{Number(product.honorario || 0)}%</Text></Col>
                    </Row>
                    <Divider style={{ margin: "14px 0 10px" }} />
                    <Row justify="space-between" align="middle">
                      <Col><Text type="secondary">Precio público</Text><br /><Text strong style={{ fontSize: 16 }}>{money.format(Number(product.precioPublicado || 0))}</Text></Col>
                      <Col><Space><Button type="text" icon={<EditOutlined />} onClick={() => openEditModal(product)}>Editar</Button><Popconfirm title="¿Eliminar este producto?" onConfirm={() => handleDelete(product)}><Button type="text" danger icon={<DeleteOutlined />}>Eliminar</Button></Popconfirm></Space></Col>
                    </Row>
                  </Card>
                </Col>
              ))}
            </Row>
          )
        ) : (
          <Empty description="Todavía no hay productos registrados">
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>Registrar producto</Button>
          </Empty>
        )}
      </Card>

      <Modal
        open={isModalOpen}
        title={
          <Space size={10}>
            <Avatar size={34} shape="square" icon={<ShoppingOutlined />} style={{ background: "#e6f4ff", color: "#1677ff", borderRadius: 10 }} />
            <div>
              <Text strong>{editingProduct ? "Editar producto" : "Nuevo producto"}</Text>
              <br />
              <Text type="secondary" style={{ fontSize: 12 }}>Información y control de inventario</Text>
            </div>
          </Space>
        }
        okText={editingProduct ? "Guardar cambios" : "Crear producto"}
        cancelText="Cancelar"
        confirmLoading={saving}
        width={screens.md ? 820 : "calc(100% - 24px)"}
        styles={{ body: { paddingTop: 20 } }}
        onCancel={() => setIsModalOpen(false)}
        onOk={handleSave}
      >
        <Form form={form} layout="vertical" initialValues={initialValues}>
          <Text strong style={{ color: "#334155" }}>Información principal</Text>
          <Row gutter={16}>
            <Col xs={24} md={8}><Form.Item label="Clave" name="clave" rules={[{ required: true, message: "Ingresá la clave" }]}><Input size="large" placeholder="Ej. SH-001" /></Form.Item></Col>
            <Col xs={24} md={16}><Form.Item label="Nombre" name="nombre" rules={[{ required: true, message: "Ingresá el nombre" }]}><Input size="large" placeholder="Nombre del producto" /></Form.Item></Col>
          </Row>
          <Form.Item
            label="Destino del producto"
            name="usageType"
            rules={[{ required: true, message: "Seleccioná para qué se utilizará el producto" }]}
            extra="Los productos de uso interno no aparecerán en la app de los estilistas ni podrán registrarse como venta."
          >
            <Select
              size="large"
              options={[
                { value: "sale", label: "Para vender" },
                { value: "internal", label: "Uso interno del salón" },
              ]}
            />
          </Form.Item>
          <Form.Item label="Descripción" name="descripcion"><Input.TextArea rows={3} placeholder="Presentación, marca o detalles relevantes" /></Form.Item>
          <Form.Item label="Imagen del producto" extra="Formatos admitidos: JPG y PNG.">
            <Upload
              accept="image/png,image/jpeg"
              listType="picture"
              maxCount={1}
              fileList={fileList}
              beforeUpload={() => false}
              onChange={({ fileList: nextFileList }) => setFileList(nextFileList.slice(-1))}
            >
              <Button icon={<UploadOutlined />}>Seleccionar foto</Button>
            </Upload>
            {editingProduct?.imagen && !fileList.length && (
              <Space style={{ marginTop: 8 }}>
                <Image src={editingProduct.imagen} width={44} height={44} style={{ objectFit: "cover", borderRadius: 8 }} />
                <Text type="secondary">La imagen actual se conserva si no seleccionás otra.</Text>
              </Space>
            )}
          </Form.Item>
          <Divider style={{ margin: "20px 0" }} />
          <Text strong style={{ color: "#334155" }}>Existencias y precios</Text>
          <Row gutter={16}>
            <Col xs={24} sm={8}><Form.Item label="Stock ideal" name="stock"><InputNumber size="large" min={0} precision={0} style={{ width: "100%" }} /></Form.Item></Col>
            <Col xs={24} sm={8}><Form.Item label="Existencia actual" name="existencia"><InputNumber size="large" min={0} precision={0} style={{ width: "100%" }} /></Form.Item></Col>
            <Col xs={24} sm={8}><Form.Item label="Honorario" name="honorario"><InputNumber size="large" min={0} max={100} suffix="%" style={{ width: "100%" }} /></Form.Item></Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} sm={12}><Form.Item label="Precio lista" name="precioLista"><InputNumber size="large" min={0} prefix="$" style={{ width: "100%" }} /></Form.Item></Col>
            <Col xs={24} sm={12}><Form.Item label="Precio publicado" name="precioPublicado"><InputNumber size="large" min={0} prefix="$" style={{ width: "100%" }} /></Form.Item></Col>
          </Row>
          <div style={{ padding: "10px 12px", borderRadius: 10, background: "#f8fafc" }}>
            <Text type="secondary">El faltante se calcula automáticamente según el stock ideal y la existencia actual.</Text>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default Products;
